import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QuoteManagement.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const getAxiosConfig = () => ({
  headers: {
    'X-Auth-Token': localStorage.getItem('authToken') || ''
  }
});

const QuoteManagement = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/quotes/list`, getAxiosConfig());
      setQuotes(response.data.quotes || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
      showMessage('Failed to load quotes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 3000);
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/quotes/${quoteId}/status`, { status: newStatus }, getAxiosConfig());
      await loadQuotes();
      showMessage('Quote status updated');
    } catch (error) {
      showMessage('Failed to update quote status', 'error');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;

    try {
      await axios.delete(`${API_URL}/api/quotes/${quoteId}`, getAxiosConfig());
      await loadQuotes();
      setSelectedQuote(null);
      showMessage('Quote deleted');
    } catch (error) {
      showMessage('Failed to delete quote', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffb800',
      reviewed: '#3b82f6',
      quoted: '#8b5cf6',
      approved: '#10b981',
      rejected: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="quotes-loading">Loading quotes...</div>;
  }

  return (
    <div className="quote-management">
      <div className="quotes-header">
        <h2>Quote Requests ({quotes.length})</h2>
        <button onClick={loadQuotes} className="btn btn-secondary">
          Refresh
        </button>
      </div>

      {message && (
        <div className={`quote-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {quotes.length === 0 ? (
        <div className="no-quotes">
          <p>No quote requests yet</p>
        </div>
      ) : (
        <div className="quotes-layout">
          <div className="quotes-list">
            {quotes.map((quote) => (
              <div
                key={quote.quoteId}
                className={`quote-card ${selectedQuote?.quoteId === quote.quoteId ? 'selected' : ''}`}
                onClick={() => setSelectedQuote(quote)}
              >
                <div className="quote-card-header">
                  <span className="quote-id">{quote.quoteId}</span>
                  <span
                    className="quote-status-badge"
                    style={{ backgroundColor: getStatusColor(quote.status) }}
                  >
                    {quote.status}
                  </span>
                </div>
                <div className="quote-customer">
                  <strong>{quote.customerInfo.name}</strong>
                  <span>{quote.customerInfo.company}</span>
                </div>
                <div className="quote-date">{formatDate(quote.timestamp)}</div>
                {quote.pricing.totalPrice && (
                  <div className="quote-price">${quote.pricing.totalPrice.toFixed(2)}</div>
                )}
              </div>
            ))}
          </div>

          {selectedQuote && (
            <div className="quote-details">
              <div className="quote-details-header">
                <h3>Quote Details</h3>
                <button onClick={() => setSelectedQuote(null)} className="close-btn">×</button>
              </div>

              <div className="quote-section">
                <h4>Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{selectedQuote.customerInfo.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <a href={`mailto:${selectedQuote.customerInfo.email}`}>
                      {selectedQuote.customerInfo.email}
                    </a>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <a href={`tel:${selectedQuote.customerInfo.phone}`}>
                      {selectedQuote.customerInfo.phone}
                    </a>
                  </div>
                  <div className="info-item">
                    <label>Company:</label>
                    <span>{selectedQuote.customerInfo.company}</span>
                  </div>
                </div>
                {selectedQuote.customerInfo.notes && (
                  <div className="notes-section">
                    <label>Notes:</label>
                    <p>{selectedQuote.customerInfo.notes}</p>
                  </div>
                )}
              </div>

              <div className="quote-section">
                <h4>Configuration</h4>
                <div className="config-list">
                  {selectedQuote.pricing.parts.map((part, index) => (
                    <div key={index} className="config-item">
                      <span className="config-label">{part.stepName}:</span>
                      <span className="config-value">{part.optionName}</span>
                      {part.price > 0 && (
                        <span className="config-price">+${part.price.toFixed(2)}</span>
                      )}
                    </div>
                  ))}
                </div>
                {selectedQuote.pricing.totalPrice && (
                  <div className="config-total">
                    <strong>Estimated Total:</strong>
                    <strong>${selectedQuote.pricing.totalPrice.toFixed(2)}</strong>
                  </div>
                )}
              </div>

              <div className="quote-section">
                <h4>Status Management</h4>
                <div className="status-buttons">
                  <button
                    onClick={() => updateQuoteStatus(selectedQuote.quoteId, 'pending')}
                    className="status-btn pending"
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => updateQuoteStatus(selectedQuote.quoteId, 'reviewed')}
                    className="status-btn reviewed"
                  >
                    Reviewed
                  </button>
                  <button
                    onClick={() => updateQuoteStatus(selectedQuote.quoteId, 'quoted')}
                    className="status-btn quoted"
                  >
                    Quoted
                  </button>
                  <button
                    onClick={() => updateQuoteStatus(selectedQuote.quoteId, 'approved')}
                    className="status-btn approved"
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => updateQuoteStatus(selectedQuote.quoteId, 'rejected')}
                    className="status-btn rejected"
                  >
                    Rejected
                  </button>
                </div>
              </div>

              <div className="quote-actions">
                <button
                  onClick={() => deleteQuote(selectedQuote.quoteId)}
                  className="btn btn-danger"
                >
                  Delete Quote
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteManagement;
